import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { LABELS } from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { DeleteForever } from "@/components/delete";
import { CreateCycleAction } from "@/components/prop-onboarding";
import { DataTable, type DataTableColumn } from "@/components/table";
import { ApproximateNote, HoldButton, HoldNote, PHASE_LABEL, StateBadge } from "@/components/session";
import { Button, ErrorNote, inputClass, useCanWrite } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { money } from "@/lib/format";
import { usePropFirms, useSessions } from "@/lib/queries";
import type { SessionListItem } from "@/lib/types";

/*
 * Fasi: prop contro broker dentro il ciclo, fase per fase.
 *
 * Nessuna Fase nuova nasce da un dato che non c'era già in `SessionListItem`:
 * le tre colonne di avanzamento (target, DD residuo, recover) sono
 * `summary.targetRemaining` / `summary.ddRemaining` / `summary.recoverRemaining`,
 * gli stessi numeri che prima stavano dentro le barre di `Progress` — qui sono
 * colonne, ordinabili come le altre.
 *
 * Questa pagina non è nell'allowlist del CLIENT (`App.tsx`): non c'è una vista
 * sua da preservare, a differenza di Cicli.
 */

type StatoFiltro = "TUTTE" | "ATTIVE" | "PAYOUT" | "COMPLETED" | "FAILED";

const STATO_OPZIONI: { key: StatoFiltro; label: string }[] = [
  { key: "TUTTE", label: "Tutte" },
  { key: "ATTIVE", label: "Attive" },
  { key: "PAYOUT", label: LABELS.sessionState.AWAITING_PAYOUT },
  { key: "COMPLETED", label: LABELS.sessionState.COMPLETED },
  { key: "FAILED", label: LABELS.sessionState.FAILED },
];

const PHASE_RANK: Record<string, number> = { PHASE_1: 1, PHASE_2: 2, FUNDED: 3 };

/** Pulsanti mutuamente esclusivi: non una tendina. Stessa forma in Cicli. */
function FilterButtons<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string }[];
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1 rounded border border-line bg-raised p-1">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={[
            "rounded px-2.5 py-1 text-xs font-medium transition-colors",
            value === o.key ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
          ].join(" ")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Sessions() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const sessions = useSessions({});
  const propFirms = usePropFirms();
  const [stato, setStato] = useState<StatoFiltro>("ATTIVE");
  const [propFirmId, setPropFirmId] = useState("");
  const [search, setSearch] = useState("");

  const all = useMemo(() => sessions.data ?? [], [sessions.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((s) => {
      if (stato === "ATTIVE" && !(s.state === "ACTIVE" || s.state === "PAUSED")) return false;
      if (stato === "PAYOUT" && s.state !== "AWAITING_PAYOUT") return false;
      if (stato === "COMPLETED" && s.state !== "COMPLETED") return false;
      if (stato === "FAILED" && s.state !== "FAILED") return false;
      if (propFirmId && (s.preset ?? s.cycle?.preset)?.propFirm.id !== propFirmId) return false;
      if (
        q &&
        ![s.client.name, s.cycle?.label ?? "", s.propAccount.label, s.brokerAccount?.label ?? "", s.propAccount.mt5Login, s.brokerAccount?.mt5Login ?? ""].some((v) =>
          v.toLowerCase().includes(q),
        )
      )
        return false;
      return true;
    });
  }, [all, stato, propFirmId, search]);

  const columns: DataTableColumn<SessionListItem>[] = [
    {
      key: "client",
      header: "Cliente",
      cell: (s) => <span className="font-medium text-ink">{s.client.name}</span>,
      sortBy: (s) => s.client.name.toLowerCase(),
    },
    {
      key: "cycle",
      header: "Ciclo",
      // Senza ciclo la Fase opera senza contabilità: si dice, non si lascia un trattino che sembra un dato mancante.
      cell: (s) => (s.cycle ? (s.cycle.label ?? <span className="text-faint">—</span>) : <span className="text-faint">senza contabilità</span>),
      sortBy: (s) => (s.cycle?.label ?? "").toLowerCase(),
    },
    { key: "phase", header: "Fase", cell: (s) => PHASE_LABEL(s.phase), sortBy: (s) => PHASE_RANK[s.phase] ?? 0 },
    { key: "prop", header: "Prop", cell: (s) => <span className="text-prop">{s.propAccount.label}</span>, sortBy: (s) => s.propAccount.label.toLowerCase() },
    {
      key: "broker",
      header: "Broker",
      cell: (s) => (s.brokerAccount ? <span className="text-broker">{s.brokerAccount.label}</span> : <span className="text-faint">in attesa del conto</span>),
      sortBy: (s) => (s.brokerAccount?.label ?? "").toLowerCase(),
    },
    { key: "symbol", header: "Simbolo predefinito", cell: (s) => s.canonicalSymbol, sortBy: (s) => s.canonicalSymbol },
    {
      key: "target",
      header: "Manca al target",
      align: "right",
      cell: (s) => (s.summary.targetRemaining === null ? <span className="text-faint">—</span> : money(s.summary.targetRemaining, s.propAccount.currency)),
      sortBy: (s) => s.summary.targetRemaining,
    },
    {
      key: "ddRemaining",
      header: "Manca a bruciare",
      align: "right",
      cell: (s) => (
        <span>
          {money(s.summary.ddRemaining, s.propAccount.currency)}
          {s.summary.ddUsedIsApproximate && <ApproximateNote />}
        </span>
      ),
      sortBy: (s) => s.summary.ddRemaining,
    },
    {
      key: "recover",
      header: LABELS.terms.recoverAmount,
      align: "right",
      cell: (s) =>
        s.summary.recoverRemaining === null || !s.cycle ? (
          <span className="text-faint">—</span>
        ) : (
          <span>
            {s.summary.costsRecoveredFxAssumed ? "≈ " : ""}
            {money(s.summary.recoverRemaining, s.cycle.currency)}
          </span>
        ),
      sortBy: (s) => s.summary.recoverRemaining,
    },
    { key: "opsToday", header: "Oggi", align: "right", cell: (s) => s.summary.opsToday, sortBy: (s) => s.summary.opsToday },
    {
      key: "state",
      header: "Stato",
      cell: (s) => (
        <div>
          <StateBadge kind="session" state={s.state} />
          {s.state === "PAUSED" && <HoldNote hold={{ reason: s.holdReason, note: s.holdNote, since: s.holdSince }} className="mt-0.5 block" />}
        </div>
      ),
      sortBy: (s) => s.state,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (s) => (
        // stopPropagation: la riga naviga alla Fase allo stesso posto di "Apri",
        // e i pulsanti qui dentro non devono farlo scattare due volte.
        <span className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/fasi/${s.id}`} className="rounded px-2 py-1 text-xs font-medium text-muted hover:bg-raised hover:text-ink">
            Apri
          </Link>
          {canWrite && (s.state === "ACTIVE" || s.state === "PAUSED") && <HoldButton session={s} size="sm" />}
          {canWrite && <FaseMenu session={s} />}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Fasi" subtitle="Prop contro broker dentro il ciclo, fase per fase" />

      <div className="space-y-4 px-6 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <FilterButtons label="Stato della fase" value={stato} onChange={setStato} options={STATO_OPZIONI} />
          <FilterButtons
            label="Prop firm"
            value={propFirmId}
            onChange={setPropFirmId}
            options={[{ key: "", label: "Tutte le firm" }, ...(propFirms.data ?? []).map((f) => ({ key: f.id, label: f.name }))]}
          />
          <input
            aria-label="Cerca cliente, ciclo o conto"
            placeholder="Cerca cliente, ciclo o conto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputClass} w-64 py-1.5`}
          />
        </div>

        {sessions.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : sessions.error ? (
          <ErrorNote>{errorMessage(sessions.error)}</ErrorNote>
        ) : (
          <div className="card">
            <DataTable
              columns={columns}
              rows={filtered}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate(`/fasi/${s.id}`)}
              empty={
                all.length === 0
                  ? {
                      title: "Nessuna fase attiva",
                      hint: canWrite
                        ? "Una fase nasce dalla scheda di un cliente, col pulsante «Aggiungi conto»."
                        : "Una fase lega il conto prop di un cliente al suo conto broker e fissa lotto di partenza, moltiplicatore, stop loss e take profit.",
                      action: canWrite && (
                        <Link to="/clienti" className="inline-flex rounded bg-accent px-3 py-1.5 text-sm font-medium text-page hover:bg-accent/90">
                          Vai ai clienti
                        </Link>
                      ),
                    }
                  : { title: "Nessuna fase con questi filtri", hint: "Cambia stato, firm o cerca un altro cliente, ciclo o conto." }
              }
            />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// «…»: Modifica parametri, Fase superata, Conto bruciato, Elimina
// ---------------------------------------------------------------------------

function MenuLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} role="menuitem" className="block rounded px-2 py-1.5 text-sm text-ink hover:bg-raised">
      {children}
    </Link>
  );
}

/**
 * «Modifica parametri», «Fase superata» e «Conto bruciato» restano dove sono,
 * su `/fasi/:id`: i loro form sono già lì (`ParamsTab`, i pulsanti d'esito
 * nell'intestazione) e non sono componenti condivisi da montare qui. Stessa
 * scelta della scheda cliente (`ClientDetail.tsx`, `FaseMenu`).
 */
function FaseMenu({ session }: { session: SessionListItem }) {
  return (
    <MoreMenu label={`Altre azioni sulla fase di ${session.client.name}`}>
      <MenuLink to={`/fasi/${session.id}`}>Modifica parametri</MenuLink>
      {/* Solo su una Fase senza ciclo: su Fase 2 e Funded la voce c'è ma è disabilitata, col motivo. */}
      {session.state !== "AWAITING_PAYOUT" && <CreateCycleAction session={session} variant="menu" />}
      <MenuLink to={`/fasi/${session.id}`}>{LABELS.sessionState.COMPLETED}</MenuLink>
      <MenuLink to={`/fasi/${session.id}`}>{LABELS.sessionState.FAILED}</MenuLink>
      <div className="px-1 py-0.5">
        <DeleteForever kind="fase" id={session.id} label="questa Fase" size="sm" />
      </div>
    </MoreMenu>
  );
}

/**
 * Si chiude al clic fuori o con Esc — MAI a un clic dentro: dentro vive
 * `DeleteForever`, un flusso a più passi (anteprima, conferma). Stessa forma
 * di `ClientDetail.tsx`, componente locale e non condiviso: non c'era prima.
 */
function MoreMenu({ children, label }: { children: ReactNode; label: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <Button size="sm" variant="ghost" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-20 mt-1 w-56 space-y-1 rounded border border-line bg-surface p-1.5 shadow-lg">
          {children}
        </div>
      )}
    </div>
  );
}
